import { useCallback, useEffect, useState } from 'react';

import { HEARTBEAT_STALE_MS, runnerLiveness } from '@nightshift/ui-liveness';
import type { MachineList, MachineListRow } from '@nightshift/ui-types';

import { Empty, Kicker, RefreshError, Since, Spinner } from '../components/primitives.tsx';
import { api, type SessionUser } from '../live/api.ts';
import { useLive } from '../live/context.tsx';
import { harnessLoginChip } from '../lib/harness-login.ts';
import './sheet.css';
import './agents.css';
import { HarnessCliPanel } from './agents-harness-cli.tsx';

type LampTone = 'ok' | 'attention' | 'quiet';

function lampFor(row: MachineListRow, now: number): { tone: LampTone; label: string } {
  const liveness = runnerLiveness(
    { status: row.health, lastHeartbeatAt: row.lastSeen },
    now,
    HEARTBEAT_STALE_MS,
  );
  if (row.paused) return { tone: 'quiet', label: 'paused' };
  if (row.health === 'unhealthy') return { tone: 'attention', label: 'unhealthy' };
  if (row.health === 'online' && !liveness.live) return { tone: 'attention', label: 'stale' };
  if (liveness.live) return { tone: 'ok', label: 'online' };
  return { tone: 'quiet', label: row.health };
}

function lampColour(tone: LampTone): string {
  if (tone === 'ok') return 'var(--good)';
  if (tone === 'attention') return 'var(--warn)';
  return 'var(--text-4)';
}

/**
 * Machine registry. The rail still routes here as Agents so the entry does not 404.
 */
export function AgentsView({ user }: { user: SessionUser | null }) {
  const live = useLive();
  const [page, setPage] = useState<MachineList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);

  const poke = `${live.snapshot?.revision ?? 0}:${live.lastContactAt}`;

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const next = await api.machines();
      setPage(next);
      setLoadedAt(Date.now());
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, poke]);

  if (error && !page) {
    return (
      <Empty
        title="The machine list could not be read."
        body={error}
        action={
          <button type="button" className="btn" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  }

  if (!page) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', flex: 1 }}>
        <Spinner size={20} />
      </div>
    );
  }

  return (
    <div className="sheet machines-index" aria-busy={busy}>
      <div className="sheet-bar">
        <Kicker count={page.items.length}>Machines</Kicker>
      </div>
      {error ? (
        <RefreshError
          what="The machine list"
          error={error}
          since={loadedAt}
          now={live.tick}
          onRetry={() => void load()}
        />
      ) : null}
      <div className="sheet-body">
        {page.items.length === 0 ? (
          <Empty
            title="No machines registered."
            body="Add a host from Settings → Machines and run the downloaded installer. It appears here after the first register and heartbeat."
          />
        ) : (
          <div className="sheet-panel">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Health</th>
                  <th>OS</th>
                  <th>Version</th>
                  <th>Harnesses</th>
                  <th>Workers</th>
                  <th title="Sidecar slots. Separate from workers: a sidecar throttles nothing.">
                    Sidecars
                  </th>
                  <th>Last seen</th>
                  <th title="Attempts and sidecar jobs this machine is running right now.">
                    Running now
                  </th>
                </tr>
              </thead>
              <tbody>
                {page.items.map((row) => (
                  <MachineRow key={row.id} row={row} now={live.tick} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {page.items.length > 0 ? <HarnessCliPanel user={user} /> : null}
      </div>
    </div>
  );
}

function MachineRow({ row, now }: { row: MachineListRow; now: number }) {
  const lamp = lampFor(row, now);
  const colour = lampColour(lamp.tone);
  const attempts =
    row.currentAttempts.length === 0 && row.currentSidecars.length === 0
      ? null
      : [
          ...row.currentAttempts.map((attempt) => `${attempt.ticketKey} · ${attempt.status}`),
          // Named as sidecars: the column is headed by neither lane, and "MONO-14 · footprint"
          // alongside "MONO-7 · Running" otherwise reads as an attempt in a state called
          // footprint.
          ...row.currentSidecars.map(
            (job) =>
              `${job.ticketKey} · sidecar ${job.kind === 'footprint' ? 'footprint' : job.kind === 'brief-cleanup' ? 'brief cleanup' : job.kind === 'creation-draft' ? 'quick add' : 'scan'}`,
          ),
        ].join('  ·  ');

  const versionBehind = row.versionStatus === 'outdated' || row.versionStatus === 'patch';

  return (
    <tr
      data-attention={
        lamp.tone === 'attention' || row.versionStatus === 'outdated' || row.harnessAuth.some((entry) => entry.state === 'failed')
          ? 'true'
          : undefined
      }
    >
      <td className="mono machines-name">{row.name}</td>
      <td>
        <span className="machines-health">
          <span
            className={`lamp${lamp.tone === 'attention' ? ' lamp-breathe' : ''}`}
            style={{ background: colour }}
            aria-hidden
          />
          <span className={lamp.tone === 'attention' ? 'machines-health-attention' : 'dim'}>
            {lamp.label}
          </span>
        </span>
      </td>
      <td className="dim">{row.os}</td>
      <td>
        {versionBehind ? (
          <span
            className="machines-health-attention mono"
            title={`Runner reports ${row.version ?? 'no version'}; orchestrator is ${row.expectedVersion}. ${
              row.versionStatus === 'outdated' ? 'Refused work until it updates.' : 'Behind by a patch.'
            }`}
          >
            {row.version ?? 'unknown'}, expected {row.expectedVersion}
          </span>
        ) : (
          <span className={row.version ? 'mono dim' : 'machines-muted'}>{row.version ?? '—'}</span>
        )}
      </td>
      <td>
        {row.installedHarnesses.length === 0 ? (
          <span className="machines-muted">—</span>
        ) : (
          <span className="machines-harnesses">
            {row.installedHarnesses.map((name) => {
              const chip = harnessLoginChip(name, row.harnessAuth.find((entry) => entry.harness === name), now);
              return (
                <span className={`chip machines-harness is-${chip.tone}`} key={name} title={chip.title} data-login={chip.tone}>
                  {name}
                  {chip.suffix ? <span className="machines-harness-state">{chip.suffix}</span> : null}
                </span>
              );
            })}
          </span>
        )}
      </td>
      <td className="num">{row.maxWorkers}</td>
      <td className="num">
        {/* Zero means the runner predates sidecars, so it is offered none, not that it is idle. */}
        {row.maxSidecars === 0 ? (
          <span className="machines-muted" title="This runner reports no sidecar capacity">
            —
          </span>
        ) : (
          `${row.currentSidecars.length} / ${row.maxSidecars}`
        )}
      </td>
      <td className="num">
        {row.lastSeen === null ? (
          <span className="machines-muted">never</span>
        ) : (
          <Since at={row.lastSeen} now={now} />
        )}
      </td>
      <td>
        {attempts ? attempts : <span className="machines-muted">None</span>}
      </td>
    </tr>
  );
}
